import axios from 'axios';
import { auth } from '@/config/firebase';

const API_URL = import.meta.env.VITE_SERVER_URL + '/v1';

export const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// ── Rsync token routing ─────────────────────────────────────────────────────
// AuthContext registers these when the active provider changes.

let _activeAuthProvider: 'firebase' | 'rsync' | null = null;
let _rsyncGetToken: (() => string | null) | null = null;
let _rsyncRefresher: (() => Promise<boolean>) | null = null;
let _logoutCallback: (() => void) | null = null;

export function setActiveAuthProvider(provider: 'firebase' | 'rsync' | null) {
    _activeAuthProvider = provider;
}

export function setRsyncTokenGetter(fn: (() => string | null) | null) {
    _rsyncGetToken = fn;
}

export function setRsyncRefresher(fn: (() => Promise<boolean>) | null) {
    _rsyncRefresher = fn;
}

export function setLogoutCallback(fn: (() => void) | null) {
    _logoutCallback = fn;
}

// ── Token retrieval ─────────────────────────────────────────────────────────

export const getAuthToken = (): Promise<string | null> => {
    if (import.meta.env.VITE_MOCK_AUTH === "true") {
        return Promise.resolve("mock-token");
    }

    // Rsync path: access token lives in-memory (rsync-auth-react)
    if (_activeAuthProvider === 'rsync' && _rsyncGetToken) {
        return Promise.resolve(_rsyncGetToken());
    }

    // Firebase path: get a fresh ID token from Firebase SDK
    return new Promise((resolve) => {
        if (auth.currentUser) {
            auth.currentUser.getIdToken().then(resolve).catch(() => resolve(null));
        } else {
            const unsubscribe = auth.onAuthStateChanged(async (user) => {
                unsubscribe();
                if (user) {
                    resolve(await user.getIdToken().catch(() => null));
                } else {
                    resolve(null);
                }
            });
        }
    });
};

// ── Token refresh interceptor (Rsync only) ───────────────────────────────────
// Catches 401s, attempts a silent token refresh, retries the original request.
// Concurrent requests that 401 during a refresh are queued and replayed together.

let _isRefreshing = false;
let _refreshQueue: Array<(token: string | null) => void> = [];

function processQueue(token: string | null) {
    _refreshQueue.forEach(resolve => resolve(token));
    _refreshQueue = [];
}

api.interceptors.response.use(
    response => response,
    async (error) => {
        const originalRequest = error.config;

        // Only handle 401s for Rsync provider — Firebase refreshes itself via getIdToken()
        if (
            error.response?.status !== 401 ||
            _activeAuthProvider !== 'rsync' ||
            originalRequest._retry
        ) {
            return Promise.reject(error);
        }

        if (_isRefreshing) {
            // Queue this request until the in-progress refresh completes
            return new Promise((resolve, reject) => {
                _refreshQueue.push((token) => {
                    if (token) {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(api(originalRequest));
                    } else {
                        reject(error);
                    }
                });
            });
        }

        originalRequest._retry = true;
        _isRefreshing = true;

        try {
            const refreshed = _rsyncRefresher ? await _rsyncRefresher() : false;
            const newToken = refreshed && _rsyncGetToken ? _rsyncGetToken() : null;

            if (!newToken) {
                processQueue(null);
                _logoutCallback?.();
                return Promise.reject(error);
            }

            processQueue(newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
        } catch {
            processQueue(null);
            _logoutCallback?.();
            return Promise.reject(error);
        } finally {
            _isRefreshing = false;
        }
    }
);

// ── Request interceptor — attach auth token ──────────────────────────────────
// Add request interceptor to include auth token
api.interceptors.request.use(async (config) => {
    try {
        const token = await getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        } else {
            console.warn("No auth token available for request:", config.url);
        }
    } catch (error) {
        console.error("Error attaching auth token", error);
    }
    return config;
});
